<template>
  <pwa-layout :title="vehicle ? vehicle.registration_number : '...'" subtitle="DÉTAIL VÉHICULE" :show-back="true">

    <div v-if="loading" class="pwa-loading"><span class="mdi mdi-loading mdi-spin"></span></div>

    <div v-else-if="vehicle" class="pwa-vd">

      <!-- ── Hero fixe ── -->
      <div class="pwa-vd-hero" :class="vehicle.is_blocked ? 'pwa-vd-hero--blocked' : ''">
        <div class="pwa-vd-hero__icon">
          <span class="mdi" :class="vehicle.is_blocked ? 'mdi-car-off' : 'mdi-car'"></span>
        </div>
        <div class="pwa-vd-hero__main">
          <div class="pwa-vd-hero__reg">{{ vehicle.registration_number }}</div>
          <div class="pwa-vd-hero__model">{{ vehicleModel }}</div>
        </div>
        <span class="pwa-vd-hero__chip" :class="vehicle.is_blocked ? 'pwa-chip-blocked' : 'pwa-chip-active'">
          {{ vehicle.is_blocked ? 'BLOQUÉ' : 'EN SERVICE' }}
        </span>
      </div>

      <!-- ── Contenu scrollable ── -->
      <div class="pwa-vd-scroll">

        <!-- Informations -->
        <div class="pwa-vd-section">
          <div class="pwa-vd-section__title">Informations</div>
          <div class="pwa-vd-card">
            <div v-if="vehicle.make || vehicle.model" class="pwa-vd-row">
              <span class="pwa-vd-row__label"><span class="mdi mdi-car-info"></span> Marque / Modèle</span>
              <span class="pwa-vd-row__val">{{ vehicleModel }}</span>
            </div>
            <div v-if="vehicle.year" class="pwa-vd-row">
              <span class="pwa-vd-row__label"><span class="mdi mdi-calendar"></span> Année</span>
              <span class="pwa-vd-row__val">{{ vehicle.year }}</span>
            </div>
            <div v-if="vehicle.color" class="pwa-vd-row">
              <span class="pwa-vd-row__label"><span class="mdi mdi-palette"></span> Couleur</span>
              <span class="pwa-vd-row__val">{{ vehicle.color }}</span>
            </div>
            <div v-if="vehicle.fuel_type" class="pwa-vd-row">
              <span class="pwa-vd-row__label"><span class="mdi mdi-gas-station"></span> Carburant</span>
              <span class="pwa-vd-row__val">{{ vehicle.fuel_type }}</span>
            </div>
            <div v-if="vehicle.reservoir_capacity" class="pwa-vd-row">
              <span class="pwa-vd-row__label"><span class="mdi mdi-cup"></span> Réservoir</span>
              <span class="pwa-vd-row__val">{{ vehicle.reservoir_capacity }} L</span>
            </div>
            <div v-if="vehicle.monthly_limit" class="pwa-vd-row">
              <span class="pwa-vd-row__label"><span class="mdi mdi-chart-line"></span> Limite mensuelle</span>
              <span class="pwa-vd-row__val">{{ formatAmount(vehicle.monthly_limit) }} F</span>
            </div>
            <div v-if="vehicle.department_object && vehicle.department_object.name" class="pwa-vd-row">
              <span class="pwa-vd-row__label"><span class="mdi mdi-domain"></span> Département</span>
              <span class="pwa-vd-row__val">{{ vehicle.department_object.name }}</span>
            </div>
          </div>
        </div>

        <!-- Chauffeur -->
        <div class="pwa-vd-section">
          <div class="pwa-vd-section__title">Chauffeur</div>
          <div class="pwa-vd-card">
            <div class="pwa-vd-row">
              <span class="pwa-vd-row__label"><span class="mdi mdi-account"></span> Nom</span>
              <span class="pwa-vd-row__val">{{ driverName }}</span>
            </div>
            <div v-if="vehicle.driver_phone_number" class="pwa-vd-row">
              <span class="pwa-vd-row__label"><span class="mdi mdi-phone"></span> Téléphone</span>
              <span class="pwa-vd-row__val">{{ vehicle.driver_phone_number }}</span>
            </div>
          </div>
        </div>

        <!-- Actions -->
        <div class="pwa-vd-section">
          <div class="pwa-vd-section__title">Actions</div>
          <div class="pwa-vd-actions">

            <!-- Bloquer / Débloquer -->
            <button
              class="pwa-action-btn"
              :class="vehicle.is_blocked ? 'pwa-action-btn--green' : 'pwa-action-btn--orange'"
              :disabled="!!actionLoading"
              @click="toggleBlock"
            >
              <span class="mdi" :class="vehicle.is_blocked ? 'mdi-lock-open-outline' : 'mdi-lock-outline'"></span>
              {{ vehicle.is_blocked ? 'Débloquer le véhicule' : 'Bloquer le véhicule' }}
              <span v-if="actionLoading === 'block'" class="mdi mdi-loading mdi-spin pwa-action-spin"></span>
            </button>

            <!-- Modifier -->
            <button
              class="pwa-action-btn pwa-action-btn--dark"
              @click="$router.push({ name: 'PwaNewVehicle', query: { edit: vehicle.id } })"
            >
              <span class="mdi mdi-pencil-outline"></span>
              Modifier le véhicule
            </button>

            <!-- Télécharger badge -->
            <button
              class="pwa-action-btn pwa-action-btn--blue"
              :disabled="actionLoading === 'badge'"
              @click="downloadBadge"
            >
              <span class="mdi mdi-download-outline"></span>
              Télécharger le badge
              <span v-if="actionLoading === 'badge'" class="mdi mdi-loading mdi-spin pwa-action-spin"></span>
            </button>

            <!-- Transactions -->
            <button class="pwa-action-btn pwa-action-btn--slate" @click="goToTransactions">
              <span class="mdi mdi-history"></span>
              Voir les transactions
            </button>

            <!-- Supprimer (seulement si pas de bons d'achat actifs) -->
            <button
              v-if="!vehicle.purchase_vouchers || !vehicle.purchase_vouchers.length"
              class="pwa-action-btn pwa-action-btn--danger"
              :disabled="!!actionLoading"
              @click="showDeleteConfirm = true"
            >
              <span class="mdi mdi-delete-outline"></span>
              Supprimer le véhicule
              <span v-if="actionLoading === 'delete'" class="mdi mdi-loading mdi-spin pwa-action-spin"></span>
            </button>

          </div>
        </div>

      </div><!-- /pwa-vd-scroll -->
    </div>

    <div v-else-if="!loading" class="pwa-empty">Véhicule introuvable</div>

    <!-- Modal confirmation suppression -->
    <div v-if="showDeleteConfirm" class="pwa-modal-overlay" @click.self="showDeleteConfirm = false">
      <div class="pwa-modal">
        <div class="pwa-modal-header">
          <span class="pwa-modal-title">Supprimer le véhicule</span>
          <button class="pwa-modal-close" @click="showDeleteConfirm = false">
            <span class="mdi mdi-close"></span>
          </button>
        </div>
        <div class="pwa-modal-body">
          <p class="pwa-confirm-text">
            Supprimer <strong>{{ vehicle && vehicle.registration_number }}</strong> ? Cette action est irréversible.
          </p>
          <div class="pwa-confirm-actions">
            <button class="pwa-confirm-btn pwa-confirm-btn--cancel" @click="showDeleteConfirm = false">Annuler</button>
            <button class="pwa-confirm-btn pwa-confirm-btn--danger" :disabled="actionLoading === 'delete'" @click="doDelete">
              <span v-if="actionLoading === 'delete'" class="mdi mdi-loading mdi-spin"></span>
              Supprimer
            </button>
          </div>
        </div>
      </div>
    </div>

  </pwa-layout>
</template>

<script>
import PwaLayout from './PwaLayout.vue'
import CompanyVehiclesService from '@/services/CompanyVehiclesService'

export default {
  name: 'PwaVehicleDetail',
  components: { PwaLayout },
  data() {
    return {
      vehicle: null,
      loading: false,
      actionLoading: null,
      showDeleteConfirm: false,
    }
  },
  computed: {
    vehicleModel() {
      const make = this.vehicle?.make || ''
      const model = this.vehicle?.model || ''
      return [make, model].filter(Boolean).join(' ') || 'N/A'
    },
    driverName() {
      const name = `${this.vehicle?.driver_first_name || ''} ${this.vehicle?.driver_last_name || ''}`.trim()
      return name || 'Non assigné'
    },
    productLabel() {
      const v = this.vehicle
      if (!v) return 'Diesel'
      if (typeof v.product === 'object' && v.product) return v.product.name || v.product.label || 'Diesel'
      return v.fuel_type || (v.product ? String(v.product) : '') || 'Diesel'
    },
  },
  mounted() {
    this.loadVehicle()
  },
  methods: {
    async loadVehicle() {
      this.loading = true
      try {
        const data = await CompanyVehiclesService.get(this, this.$route.params.id)
        this.vehicle = Array.isArray(data) ? data[0] : (data?.results?.[0] || data)
      } catch (e) {
        console.error(e)
      } finally {
        this.loading = false
      }
    },
    async toggleBlock() {
      this.actionLoading = 'block'
      try {
        await CompanyVehiclesService.toggleBlock(this, this.vehicle.id)
        this.vehicle = { ...this.vehicle, is_blocked: !this.vehicle.is_blocked }
      } catch (e) {
        console.error(e)
      } finally {
        this.actionLoading = null
      }
    },
    async downloadBadge() {
      this.actionLoading = 'badge'
      try {
        const blobUrl = await CompanyVehiclesService.getBadge(this, this.vehicle.id, 'v1')
        const a = document.createElement('a')
        a.href = blobUrl
        a.download = `badge-${this.vehicle.registration_number}.pdf`
        a.click()
        setTimeout(() => URL.revokeObjectURL(blobUrl), 10000)
      } catch (e) {
        console.error(e)
      } finally {
        this.actionLoading = null
      }
    },
    async doDelete() {
      this.actionLoading = 'delete'
      try {
        await CompanyVehiclesService.remove(this, this.vehicle.id)
        this.$router.back()
      } catch (e) {
        console.error(e)
        this.actionLoading = null
        this.showDeleteConfirm = false
      }
    },
    goToTransactions() {
      this.$router.push({ name: 'PwaHistory', query: { search: this.vehicle.registration_number } })
    },
    formatAmount(v) {
      if (!v && v !== 0) return '0'
      return Math.round(Number(v)).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '\u00a0')
    },
  },
}
</script>

<style scoped>
.pwa-loading { text-align: center; padding: 48px; font-size: 26px; color: var(--pwa-muted); }
.pwa-empty   { text-align: center; padding: 48px; color: var(--pwa-muted); font-size: 14px; }

/* ── Layout : flex column, hauteur fixe = viewport - header - nav ── */
.pwa-vd {
  display: flex;
  flex-direction: column;
  height: calc(100dvh - var(--pwa-header-h) - var(--pwa-nav-h) - 8px);
  overflow: hidden;
}

/* ── Hero (fixe) ── */
.pwa-vd-hero {
  flex-shrink: 0;
  background: linear-gradient(135deg, #1565C0 0%, #1976D2 100%);
  margin: 16px 16px 0;
  border-radius: var(--pwa-card-radius);
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 14px;
}
.pwa-vd-hero--blocked {
  background: linear-gradient(135deg, #B71C1C 0%, #C62828 100%);
}
.pwa-vd-hero__icon {
  width: 52px; height: 52px; border-radius: 14px;
  background: rgba(255,255,255,0.18);
  display: flex; align-items: center; justify-content: center;
  font-size: 26px; color: white; flex-shrink: 0;
}
.pwa-vd-hero__main { flex: 1; min-width: 0; }
.pwa-vd-hero__reg   { font-size: 22px; font-weight: 800; color: white; letter-spacing: -0.5px; }
.pwa-vd-hero__model { font-size: 13px; color: rgba(255,255,255,0.7); margin-top: 2px; }
.pwa-vd-hero__chip  { font-size: 10px; font-weight: 700; padding: 4px 10px; border-radius: 8px; letter-spacing: 0.5px; white-space: nowrap; flex-shrink: 0; }
.pwa-chip-active, .pwa-chip-blocked { background: rgba(255,255,255,0.2); color: white; }

/* ── Scroll (tout sauf hero) ── */
.pwa-vd-scroll {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  padding: 16px 16px 16px;
}

/* Sections */
.pwa-vd-section { margin-bottom: 20px; }
.pwa-vd-section__title {
  font-size: 12px; font-weight: 700; color: var(--pwa-muted);
  text-transform: uppercase; letter-spacing: 0.8px; margin-bottom: 10px;
}
.pwa-vd-card { background: var(--pwa-white); border-radius: var(--pwa-card-radius); }
.pwa-vd-row {
  display: flex; justify-content: space-between; align-items: center;
  padding: 13px 16px; border-bottom: 1px solid #f5f5f5;
}
.pwa-vd-row:last-child { border-bottom: none; }
.pwa-vd-row__label {
  display: flex; align-items: center; gap: 8px;
  font-size: 13px; color: var(--pwa-muted); font-weight: 500;
}
.pwa-vd-row__label .mdi { font-size: 16px; }
.pwa-vd-row__val { font-size: 14px; font-weight: 600; color: var(--pwa-text); text-align: right; max-width: 55%; }

/* Actions */
.pwa-vd-actions { display: flex; flex-direction: column; gap: 10px; }
.pwa-action-btn {
  display: flex; align-items: center; gap: 10px;
  width: 100%; padding: 14px 16px;
  border: none; border-radius: 12px;
  font-size: 14px; font-weight: 700; cursor: pointer;
  transition: opacity 0.15s; text-align: left;
}
.pwa-action-btn:disabled { opacity: 0.55; cursor: not-allowed; }
.pwa-action-btn .mdi { font-size: 20px; flex-shrink: 0; }
.pwa-action-spin { margin-left: auto; }
.pwa-action-btn--green  { background: #E8F5E9; color: #2E7D32; }
.pwa-action-btn--orange { background: #FFF3E0; color: #E65100; }
.pwa-action-btn--dark   { background: var(--pwa-dark); color: white; }
.pwa-action-btn--blue   { background: #E3F2FD; color: #1565C0; }
.pwa-action-btn--slate  { background: #ECEFF1; color: #37474F; }
.pwa-action-btn--danger { background: #FFEBEE; color: #C62828; }

/* Modal */
.pwa-modal-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,0.45); z-index: 200;
  display: flex; align-items: flex-end; justify-content: center;
}
.pwa-modal {
  background: white; border-radius: 20px 20px 0 0;
  width: 100%; max-width: 480px;
  padding-bottom: env(safe-area-inset-bottom, 16px);
}
.pwa-modal-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 18px 20px 12px; border-bottom: 1px solid #f0f0f0;
}
.pwa-modal-title { font-size: 16px; font-weight: 700; color: var(--pwa-text); }
.pwa-modal-close {
  width: 32px; height: 32px; border: none; background: #f5f5f5;
  border-radius: 50%; display: flex; align-items: center; justify-content: center;
  font-size: 18px; color: var(--pwa-muted); cursor: pointer;
}
.pwa-modal-body { padding: 20px; }
.pwa-confirm-text { font-size: 14px; color: var(--pwa-text); line-height: 1.6; margin-bottom: 20px; }
.pwa-confirm-actions { display: flex; gap: 10px; }
.pwa-confirm-btn {
  flex: 1; padding: 13px; border-radius: 12px; border: none;
  font-size: 15px; font-weight: 700; cursor: pointer;
}
.pwa-confirm-btn--cancel { background: #F0F4F8; color: var(--pwa-muted); }
.pwa-confirm-btn--danger { background: #C62828; color: white; }
.pwa-confirm-btn:disabled { opacity: 0.6; }
</style>
